import type { TRssParsedItem } from '../models/rssDirectory'
import { isLowQualityFeedImageUrl } from './rssFeedImage'

/** Minimum hero width (px) from RSS metadata when the URL has no inferable size. */
const RSS_MIN_HERO_IMAGE_WIDTH = 400

function isLikelyVideoMediaUrl(url: string): boolean {
  const lower = url.trim().toLowerCase()
  if (/\.(mp4|webm|mov|m3u8|m4v)(\?|#|$)/.test(lower)) {
    return true
  }
  return /(?:^|\/\/)(?:www\.)?(?:youtube\.com|youtu\.be|vimeo\.com)\b/.test(lower)
}

/** True when an RSS item has a hero photo suitable for the story wall. */
export function rssItemHasDisplayablePhoto(
  item: Pick<TRssParsedItem, 'imageUrl' | 'imageWidth' | 'imageHeight'>
): boolean {
  const imageUrl = item.imageUrl?.trim()
  if (!imageUrl) {
    return false
  }
  if (isLikelyVideoMediaUrl(imageUrl)) {
    return false
  }
  if (isLowQualityFeedImageUrl(imageUrl)) {
    return false
  }
  const width = item.imageWidth
  if (width != null && width > 0 && width < RSS_MIN_HERO_IMAGE_WIDTH) {
    return false
  }
  return true
}

export function parseRssPubDateMs(pubDate: string | undefined): number | null {
  if (!pubDate?.trim()) { return null }
  const ms = Date.parse(pubDate)
  return Number.isFinite(ms) ? ms : null
}

/** Newest `pubDate` across parsed RSS items, or null when none are parseable. */
export function latestRssItemPubDateMs(items: readonly { pubDate?: string }[]): number | null {
  let max: number | null = null
  for (const item of items) {
    const ms = parseRssPubDateMs(item.pubDate)
    if (ms != null && (max == null || ms > max)) {
      max = ms
    }
  }
  return max
}

export function latestRssItemPublishedAtIso(items: readonly { pubDate?: string }[]): string | null {
  const ms = latestRssItemPubDateMs(items)
  return ms != null ? new Date(ms).toISOString() : null
}

export function filterRssItemsByMaxAge(items: TRssParsedItem[], maxAgeSec: number): TRssParsedItem[] {
  if (maxAgeSec <= 0) { return items }
  const cutoff = Date.now() - maxAgeSec * 1000
  const filtered = items.filter((item) => {
    const ms = parseRssPubDateMs(item.pubDate)
    /** Feeds without pubDate (e.g. g1/economia) must not produce an empty pool when max-age is set. */
    if (ms == null) { return true }
    return ms >= cutoff
  })
  return filtered.length > 0 ? filtered : items
}

/** Drops items without a hero photo and video posts (all RSS directory providers). */
export function filterRssItemsForProvider(
  items: readonly TRssParsedItem[],
  _providerId?: string
): TRssParsedItem[] {
  return items.filter(item => rssItemHasDisplayablePhoto(item))
}

export function countStoryWindows(poolLength: number, slideCount: number): number {
  if (poolLength <= 0 || slideCount <= 0) { return 0 }
  return Math.ceil(poolLength / slideCount)
}

export function sliceStoryWindow<T>(pool: readonly T[], slideCount: number, windowIndex: number): T[] {
  if (pool.length === 0 || slideCount <= 0) { return [] }
  const start = (windowIndex * slideCount) % pool.length
  const out: T[] = []
  for (let i = 0; i < slideCount && i < pool.length; i++) {
    out.push(pool[(start + i) % pool.length]!)
  }
  return out
}

/** True when the pool has more stories than one slide batch (windowing applies even when maxAgeSec is 0). */
export function shouldUseRssStoryWindows(poolLength: number, slideCount: number): boolean {
  return countStoryWindows(poolLength, slideCount) > 1
}

/**
 * Stories shown for one playback window. Cycles through the pool when it exceeds `slideCount`,
 * including when maxAgeSec is 0 (full feed, no age filter).
 */
export function resolveRssStoryWindowItems<T>(
  pool: readonly T[],
  slideCount: number,
  windowIndex: number
): { items: T[], windowCount: number } {
  if (pool.length === 0 || slideCount <= 0) {
    return { items: [], windowCount: 0 }
  }
  const windowCount = countStoryWindows(pool.length, slideCount)
  if (windowCount <= 1) {
    return { items: pool.slice(0, slideCount) as T[], windowCount }
  }
  const items = sliceStoryWindow(pool, slideCount, windowIndex)
  return {
    items: items.length > 0 ? items : pool.slice(0, slideCount) as T[],
    windowCount
  }
}
